import { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumbs from '@/components/feature/Breadcrumbs';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEOHead from '@/components/feature/SEOHead';
import { testimonials, brandMilestones, contactInfo } from '@/mocks/about';

const FORM_URL = 'https://readdy.ai/api/form/d9qq3jue3oq4jqumtojg';

export default function About() {
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [formError, setFormError] = useState('');
  const formRef = useRef<HTMLFormElement>(null);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormError('');

    const form = e.currentTarget;
    const honeypotEl = form.querySelector<HTMLInputElement>('input[name="phone_alt"]');
    if (honeypotEl && honeypotEl.value.trim() !== '') {
      setFormSubmitted(true);
      return;
    }

    try {
      const formData = new FormData(form);
      if (honeypotEl) formData.delete('phone_alt');

      const response = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } | null = null;
      try { parsed = JSON.parse(responseText); } catch { /* ignore */ }

      if (response.ok && parsed?.code === 'OK') {
        setFormSubmitted(true);
        formRef.current?.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '提交失败，请稍后重试';
        setFormError(serverMsg.includes('spam') || serverMsg.includes('form data is spam')
          ? '提交失败，请稍后重试'
          : serverMsg);
      }
    } catch {
      setFormError('网络错误，请稍后重试');
    }
  };

  return (
    <>
      <ScrollToTop />
      <SEOHead
        title="关于我们 - 开云平台"
        description="了解开云品牌故事、发展历程、用户口碑与联系方式。开云致力于为用户提供高效、安全、智能的数字化服务平台。"
        keywords="关于开云,开云品牌,开云联系方式,开云口碑"
        canonicalUrl="https://kaiyun.example.com/about"
      />

      {/* Organization Schema.org */}
      <script type="application/ld+json">
        {JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'Organization',
          name: '开云',
          url: 'https://kaiyun.example.com',
          description: '开云是值得信赖的综合服务平台，致力于为用户提供高效、安全、便捷的数字化体验。',
          address: {
            '@type': 'PostalAddress',
            streetAddress: '建国路88号开云大厦',
            addressLocality: '北京',
            addressRegion: '朝阳区',
            addressCountry: 'CN',
          },
          contactPoint: {
            '@type': 'ContactPoint',
            telephone: '+86-400-888-6666',
            contactType: 'customer service',
          },
        })}
      </script>

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-8">
            <Breadcrumbs items={[{ label: '首页', path: '/' }, { label: '关于我们' }]} />
          </div>

          {/* Hero */}
          <section className="pb-10">
            <div className="max-w-7xl mx-auto px-4 md:px-6 text-center">
              <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-900 mb-4">关于开云</h1>
              <p className="text-foreground-600 max-w-2xl mx-auto text-sm md:text-base leading-relaxed">
                开云自成立以来，始终致力于为用户提供高效、安全、智能的数字化服务平台。
                我们以技术创新为驱动，以用户体验为核心，持续打造行业领先的综合服务解决方案。
              </p>
            </div>
          </section>

          {/* Brand Story */}
          <section className="py-16 bg-background-100">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                <div>
                  <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-6">品牌故事</h2>
                  <div className="space-y-4">
                    <p className="text-foreground-700 leading-relaxed text-sm md:text-base">
                      开云品牌诞生于2018年，由一群怀揣数字化愿景的技术专家和行业精英共同创立。
                      我们深知在快速发展的数字时代，企业和个人都面临着效率提升和安全保障的双重挑战。
                    </p>
                    <p className="text-foreground-700 leading-relaxed text-sm md:text-base">
                      因此，开云从创立之初就确立了"以技术赋能，让服务更简单"的核心理念。
                      经过多年的持续研发和产品迭代，开云已发展成为覆盖数据分析、智能推荐、协同办公等
                      多元场景的综合性服务平台，赢得了超过50万用户的信赖。
                    </p>
                    <p className="text-foreground-700 leading-relaxed text-sm md:text-base">
                      展望未来，开云将继续深耕技术创新，不断优化用户体验，携手合作伙伴共同构建
                      更加开放、智能、安全的数字化生态体系。
                    </p>
                  </div>
                </div>
                <div className="rounded-xl overflow-hidden h-[380px]">
                  <img
                    src="https://readdy.ai/api/search-image?query=Modern%20minimalist%20corporate%20headquarters%20building%20interior%20with%20warm%20lighting%2C%20open%20workspace%20concept%2C%20team%20collaboration%20atmosphere%2C%20beige%20and%20cream%20interior%20design%2C%20professional%20corporate%20photography%2C%20clean%20architectural%20lines&width=800&height=600&seq=kaiyun-about-brand&orientation=landscape"
                    alt="开云品牌形象"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Milestones */}
          <section className="py-16 md:py-20">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-10 text-center">发展历程</h2>
              <div className="relative">
                <div className="absolute left-1/2 top-0 bottom-0 w-px bg-background-300 hidden md:block"></div>
                <div className="space-y-0">
                  {brandMilestones.map((m, idx) => (
                    <div key={m.year} className={`flex flex-col md:flex-row items-start gap-6 mb-10 md:mb-0 ${idx % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                      <div className={`flex-1 ${idx % 2 === 0 ? 'md:text-right' : 'md:text-left'} hidden md:block`}>
                        <div className={`p-4 rounded-xl ${idx % 2 === 0 ? 'md:mr-8' : 'md:ml-8'}`}>
                          {idx % 2 === 0 && (
                            <>
                              <h3 className="text-lg font-heading font-semibold text-foreground-900">{m.title}</h3>
                              <p className="text-sm text-foreground-600 mt-1">{m.description}</p>
                            </>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center justify-center w-full md:w-auto">
                        <div className="w-14 h-14 rounded-full bg-primary-500 text-white flex items-center justify-center font-heading font-bold text-sm">
                          {m.year}
                        </div>
                      </div>
                      <div className={`flex-1 ${idx % 2 === 0 ? 'md:text-left' : 'md:text-right'} ${idx % 2 !== 0 ? 'hidden md:block' : ''}`}>
                        <div className={`p-4 rounded-xl ${idx % 2 === 0 ? 'hidden md:block' : ''} ${idx % 2 === 0 ? '' : 'md:ml-8 md:block'} ${idx % 2 !== 0 ? 'md:mr-8' : ''}`}>
                          <div className="md:hidden mb-2">
                            <h3 className="text-lg font-heading font-semibold text-foreground-900">{m.title}</h3>
                            <p className="text-sm text-foreground-600 mt-1">{m.description}</p>
                          </div>
                          {idx % 2 !== 0 ? (
                            <div className="hidden md:block">
                              <h3 className="text-lg font-heading font-semibold text-foreground-900">{m.title}</h3>
                              <p className="text-sm text-foreground-600 mt-1">{m.description}</p>
                            </div>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* Testimonials */}
          <section className="py-16 bg-background-100">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-10 text-center">用户口碑</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {testimonials.map((t) => (
                  <div key={t.id} className="p-6 rounded-xl bg-background-50 border border-background-200/70">
                    <div className="flex items-center gap-4 mb-4">
                      <img src={t.avatar} alt={t.name} className="w-12 h-12 rounded-full object-cover" />
                      <div>
                        <p className="text-sm font-semibold text-foreground-900">{t.name}</p>
                        <p className="text-xs text-foreground-500">{t.role}</p>
                      </div>
                    </div>
                    <p className="text-sm text-foreground-700 leading-relaxed">{t.content}</p>
                    <div className="flex items-center gap-1 mt-4">
                      {[1, 2, 3, 4, 5].map((s) => (
                        <i key={s} className="ri-star-fill text-accent-500 text-sm"></i>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Contact Form & Info */}
          <section className="py-16 md:py-20">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-10 text-center">联系我们</h2>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-map-pin-line text-primary-600"></i>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-foreground-900 mb-1">公司地址</p>
                      <p className="text-sm text-foreground-600">{contactInfo.address}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-lg bg-accent-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-phone-line text-accent-600"></i>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-foreground-900 mb-1">联系电话</p>
                      <p className="text-sm text-foreground-600">{contactInfo.phone}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-lg bg-secondary-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-mail-line text-secondary-600"></i>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-foreground-900 mb-1">电子邮箱</p>
                      <p className="text-sm text-foreground-600">{contactInfo.email}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-lg bg-background-200 flex items-center justify-center flex-shrink-0">
                      <i className="ri-time-line text-foreground-600"></i>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-foreground-900 mb-1">工作时间</p>
                      <p className="text-sm text-foreground-600">{contactInfo.workingHours}</p>
                    </div>
                  </div>
                </div>

                <div className="lg:col-span-2">
                  {formSubmitted ? (
                    <div className="p-10 rounded-xl bg-accent-50 border border-accent-200/50 text-center">
                      <div className="w-16 h-16 mx-auto rounded-full bg-accent-100 flex items-center justify-center mb-4">
                        <i className="ri-check-line text-2xl text-accent-600"></i>
                      </div>
                      <h3 className="text-lg font-heading font-semibold text-foreground-900 mb-2">提交成功！</h3>
                      <p className="text-sm text-foreground-600">感谢您的留言，我们的团队将在1-2个工作日内与您联系。</p>
                      <button
                        onClick={() => setFormSubmitted(false)}
                        className="mt-6 px-6 py-2.5 rounded-lg bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                      >
                        继续留言
                      </button>
                    </div>
                  ) : (
                    <form
                      ref={formRef}
                      onSubmit={handleSubmit}
                      className="p-6 md:p-8 rounded-xl bg-background-100 border border-background-200/70"
                      data-readdy-form
                    >
                      <h3 className="text-lg font-heading font-semibold text-foreground-900 mb-6">发送消息</h3>

                      {formError && (
                        <div className="mb-6 p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">
                          {formError}
                        </div>
                      )}

                      <div className="space-y-4">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div>
                            <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">姓名 *</label>
                            <input
                              type="text"
                              id="name"
                              name="name"
                              required
                              className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-800 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all"
                              placeholder="请输入您的姓名"
                            />
                          </div>
                          <div>
                            <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">邮箱 *</label>
                            <input
                              type="email"
                              id="email"
                              name="email"
                              required
                              className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-800 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all"
                              placeholder="请输入您的邮箱"
                            />
                          </div>
                        </div>
                        <div>
                          <label htmlFor="subject" className="block text-sm font-medium text-foreground-700 mb-1.5">主题</label>
                          <input
                            type="text"
                            id="subject"
                            name="subject"
                            className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-800 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all"
                            placeholder="请输入消息主题"
                          />
                        </div>
                        <div>
                          <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1.5">消息内容 *</label>
                          <textarea
                            id="message"
                            name="message"
                            required
                            rows={5}
                            maxLength={500}
                            className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-800 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all resize-none"
                            placeholder="请输入您的消息（不超过500字）"
                          ></textarea>
                          <p className="text-xs text-foreground-400 mt-1">最多500个字符</p>
                        </div>

                        {/* Honeypot */}
                        <div className="contact-alt-field">
                          <input
                            type="text"
                            name="phone_alt"
                            tabIndex={-1}
                            autoComplete="off"
                            aria-hidden="true"
                            readOnly
                          />
                        </div>

                        <button
                          type="submit"
                          className="w-full py-3 rounded-lg bg-primary-500 text-white font-medium text-sm hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                        >
                          发送消息
                        </button>
                      </div>
                    </form>
                  )}
                </div>
              </div>
            </div>
          </section>
        </main>
        <Footer />
      </div>
    </>
  );
}